import React, { useEffect, useState } from 'react';
import { FileText, Headphones, Percent } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'slices';
import { fetchSupportSettings, updateSupportSettingsPut, updateSupportSettingsPatch, clearSupportError, clearSupportSuccess } from 'slices/support/supportSlice';

const GeneralSettings: React.FC = () => {
  const [betBonus, setBetBonus] = useState({
    level1: '0.50',
    level2: '0.40',
    level3: '0.30',
    level4: '0.20',
    level5: '0.10',
  });

  const dispatch = useDispatch<AppDispatch>();
  const { settings, loading, error, success } = useSelector((state: RootState) => state.Support);

  // Help & Support local form state
  const [supportNumber, setSupportNumber] = useState('');
  const [supportTime, setSupportTime] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [isWhatsappEnabled, setIsWhatsappEnabled] = useState(false);
  const [isTelegramEnabled, setIsTelegramEnabled] = useState(false);
  const [isMaintenanceEnabled, setIsMaintenanceEnabled] = useState(false);
  const [telegramLink, setTelegramLink] = useState('');

  // Rules & Regulations (will be populated from API)
  const [rulesNotice, setRulesNotice] = useState<string>('');
  const [withdrawalCondition, setWithdrawalCondition] = useState<string>('');
  const [runningText, setRunningText] = useState<string>('');

  const [rechargeBonus, setRechargeBonus] = useState({
    level1: '10.00',
    level2: '8.00',
    level3: '6.00',
    level4: '4.00',
    level5: '2.00',
  });

  const handleBetBonusChange = (level: string, value: string) => {
    setBetBonus(prev => ({
      ...prev,
      [level]: value
    }));
  };

  const handleRechargeBonusChange = (level: string, value: string) => {
    setRechargeBonus(prev => ({
      ...prev,
      [level]: value
    }));
  };

  // Load current settings on mount and on refresh
  useEffect(() => {
    const appId = localStorage.getItem('app_id') || localStorage.getItem('selectedAppId') || '4bad143c-4ed5-4681-adb1-e6f5b751dd97';
    if (appId) {
      console.log('Fetching support settings for appId:', appId);
      dispatch(fetchSupportSettings(appId));
    }
  }, [dispatch]);

  // Sync store -> local form state (only update when settings change, not on initial empty state)
  useEffect(() => {
    // Only update form fields if we have settings data and we're not loading
    if (settings && !loading) {
      console.log('Updating form fields with settings:', settings);
      setSupportNumber(settings.support_number || '');
      setSupportTime(settings.support_time || '');
      setWhatsappNumber(settings.whatsapp_number || '');
      setIsWhatsappEnabled(!!settings.whatsapp_enabled);
      setIsTelegramEnabled(!!settings.telegram_enabled);
      setIsMaintenanceEnabled(!!settings.maintenance_mode_enabled);
      setTelegramLink(settings.telegram_link || '');
      if (settings.terms_and_conditions) {
        setRulesNotice(settings.terms_and_conditions);
      }
      if (settings.withdrawal_condition) {
        setWithdrawalCondition(settings.withdrawal_condition);
      }
      if (settings.running_text !== undefined && settings.running_text !== null) {
        setRunningText(settings.running_text);
      }
    }
  }, [settings, loading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const appId = localStorage.getItem('app_id') || localStorage.getItem('selectedAppId') || settings?.app_id || '4bad143c-4ed5-4681-adb1-e6f5b751dd97';
    if (!appId) {
      // surface a friendly error if still missing
      dispatch(clearSupportError());
      return;
    }
    try {
      await dispatch(updateSupportSettingsPut({
        appId,
        payload: {
          whatsapp_number: whatsappNumber,
          support_number: supportNumber,
          support_time: supportTime,
          whatsapp_enabled: isWhatsappEnabled,
          telegram_enabled: isTelegramEnabled,
          telegram_link: telegramLink,
          maintenance_mode_enabled: isMaintenanceEnabled,
          terms_and_conditions: rulesNotice,
          withdrawal_condition: withdrawalCondition,
          running_text: runningText,
        }
      })).unwrap();
      // After successful save, fetch the updated data from homepage API
      dispatch(fetchSupportSettings(appId));
    } catch (error) {
      // Error is already handled by the thunk
      console.error('Failed to update settings:', error);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Help & Support */}
      <div className="bg-white rounded-lg shadow-md border-2 border-indigo-200">
        <div className="p-6 border-b border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center gap-3">
            <Headphones className="w-6 h-6 text-indigo-600" />
            <h2 className="text-xl font-semibold text-indigo-900">Help and Support</h2>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Support Number</label>
              <input value={supportNumber} onChange={(e)=>setSupportNumber(e.target.value)} type="text" placeholder="+91 1234567890" className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Support Time</label>
              <input value={supportTime} onChange={(e)=>setSupportTime(e.target.value)} type="text" placeholder="9:00 AM - 11:00 PM" className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Telegram Link</label>
              <input value={telegramLink} onChange={(e)=>setTelegramLink(e.target.value)} type="text" placeholder="https://www.t.me/telegram_id" className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Number</label>
              <input value={whatsappNumber} onChange={(e)=>setWhatsappNumber(e.target.value)} type="text" placeholder="+91 9876543210" className="w-full border rounded px-3 py-2" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Enable</label>
              <button
                type="button"
                onClick={() => setIsWhatsappEnabled(v => !v)}
                className={`relative inline-flex h-6 w-14 items-center rounded-full transition-colors ${
                  isWhatsappEnabled ? 'bg-blue-600' : 'bg-gray-200'
                }`}
                aria-pressed={isWhatsappEnabled}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    isWhatsappEnabled ? 'translate-x-8' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Telegram Enable</label>
              <button
                type="button"
                onClick={() => setIsTelegramEnabled(v => !v)}
                className={`relative inline-flex h-6 w-14 items-center rounded-full transition-colors ${
                  isTelegramEnabled ? 'bg-blue-600' : 'bg-gray-200'
                }`}
                aria-pressed={isTelegramEnabled}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    isTelegramEnabled ? 'translate-x-8' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Maintainance Mode Enable?</label>
            <button
              type="button"
              onClick={() => setIsMaintenanceEnabled(v => !v)}
              className={`relative inline-flex h-6 w-14 items-center rounded-full transition-colors ${
                isMaintenanceEnabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
              aria-pressed={isMaintenanceEnabled}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  isMaintenanceEnabled ? 'translate-x-8' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Running Text (between Help & Support and Rules & Regulations) */}
      <div className="bg-white rounded-lg shadow-md border-2 border-indigo-200">
        <div className="p-6 border-b border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-indigo-600" />
            <h2 className="text-xl font-semibold text-indigo-900">Running Text</h2>
          </div>
        </div>
        <div className="p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Running Text</label>
          <input
            type="text"
            value={runningText}
            onChange={(e) => setRunningText(e.target.value)}
            placeholder="Enter running text"
            className="w-full border rounded px-3 py-2"
          />
        </div>
      </div>

      {/* Rules & Regulations */}
      <div className="bg-white rounded-lg shadow-md border-2 border-indigo-200">
        <div className="p-6 border-b border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-indigo-600" />
            <h2 className="text-xl font-semibold text-indigo-900">Rules & Regulations</h2>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rules Notice</label>
              <textarea
                className="w-full border rounded px-3 py-2 min-h-[280px] resize-y"
                value={rulesNotice}
                onChange={(e) => setRulesNotice(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Withdrawal Condition</label>
              <textarea
                className="w-full border rounded px-3 py-2 min-h-[280px] resize-y"
                value={withdrawalCondition}
                onChange={(e) => setWithdrawalCondition(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Level Wise Bonus Setting */}
      <div className="bg-white rounded-lg shadow-md border-2 border-indigo-200">
        <div className="p-6 border-b border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center gap-3">
            <Percent className="w-6 h-6 text-indigo-600" />
            <h2 className="text-xl font-semibold text-indigo-900">Level Wise Bonus Setting</h2>
          </div>
        </div>

        <form className="p-6" onSubmit={handleSubmit}>
          {/* Bet Bonus Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Bet Bonus :-
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Level 1 commission (in %)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={betBonus.level1}
                  onChange={(e) => handleBetBonusChange('level1', e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Level 2 commission (in %)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={betBonus.level2}
                  onChange={(e) => handleBetBonusChange('level2', e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Level 3 commission (in %)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={betBonus.level3}
                  onChange={(e) => handleBetBonusChange('level3', e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Level 4 commission (in %)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={betBonus.level4}
                  onChange={(e) => handleBetBonusChange('level4', e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Level 5 commission (in %)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={betBonus.level5}
                  onChange={(e) => handleBetBonusChange('level5', e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Recharge Bonus Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Recharge Bonus :-
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Level 1 recharge commission (in %)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={rechargeBonus.level1}
                  onChange={(e) => handleRechargeBonusChange('level1', e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Level 2 recharge commission (in %)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={rechargeBonus.level2}
                  onChange={(e) => handleRechargeBonusChange('level2', e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Level 3 recharge commission (in %)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={rechargeBonus.level3}
                  onChange={(e) => handleRechargeBonusChange('level3', e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Level 4 recharge commission (in %)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={rechargeBonus.level4}
                  onChange={(e) => handleRechargeBonusChange('level4', e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Level 5 recharge commission (in %)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={rechargeBonus.level5}
                  onChange={(e) => handleRechargeBonusChange('level5', e.target.value)}
                  className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={async () => {
                const appId = localStorage.getItem('app_id') || localStorage.getItem('selectedAppId') || settings?.app_id || '4bad143c-4ed5-4681-adb1-e6f5b751dd97';
                if (!appId) return;
                const patchPayload: any = {};
                if (supportNumber !== settings?.support_number) patchPayload.support_number = supportNumber;
                if (supportTime !== settings?.support_time) patchPayload.support_time = supportTime;
                if (whatsappNumber !== settings?.whatsapp_number) patchPayload.whatsapp_number = whatsappNumber;
                if (isWhatsappEnabled !== !!settings?.whatsapp_enabled) patchPayload.whatsapp_enabled = isWhatsappEnabled;
                if (isTelegramEnabled !== !!settings?.telegram_enabled) patchPayload.telegram_enabled = isTelegramEnabled;
                if (telegramLink !== settings?.telegram_link) patchPayload.telegram_link = telegramLink;
                if (isMaintenanceEnabled !== !!settings?.maintenance_mode_enabled) patchPayload.maintenance_mode_enabled = isMaintenanceEnabled;
                if (rulesNotice !== (settings?.terms_and_conditions || '')) patchPayload.terms_and_conditions = rulesNotice;
                if (withdrawalCondition !== (settings?.withdrawal_condition || '')) patchPayload.withdrawal_condition = withdrawalCondition;
                if (runningText !== (settings?.running_text || '')) patchPayload.running_text = runningText;
                if (Object.keys(patchPayload).length === 0) return;
                try {
                  await dispatch(updateSupportSettingsPatch({ appId, payload: patchPayload })).unwrap();
                  // After successful save, fetch the updated data from homepage API
                  dispatch(fetchSupportSettings(appId));
                } catch (error) {
                  // Error is already handled by the thunk
                  console.error('Failed to update settings:', error);
                }
              }}
              className="ml-3 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              Patch Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GeneralSettings;

